import { Boxes, CheckCircle2, ChevronDown, ChevronUp, Download, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TablePagination from '../components/TablePagination';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePagination } from '../hooks/usePagination';
import SchucoPredictionCorrection from '../components/SchucoPredictionCorrection';
import { schucoTrackingService } from '../services/api';
import {
  getDeliveryHistoryTooltip,
  getDeliveryStatusClass,
  isDelayed,
  parsePLDate
} from './utils/ProductionSchucoMonitoring';

const TABS = [
  { key: 'pending', label: 'Oczekujące materiały' },
  { key: 'predictions', label: 'Predykcja dostaw' },
  { key: 'complaints', label: 'Reklamacja' },
];

const formatDateInputValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getComplaintRowKey = (item) => (
  `${item.order_number}:${item.position}:${item.item_number}:${item.estimated_delivery_date || ''}`
);

const getDefaultComplaintDeliveryDate = (item) => {
  const orderDate = parsePLDate(item.order_date);
  let estimatedDate = parsePLDate(item.estimated_delivery_date);

  if (!estimatedDate && orderDate && item.estimated_delivery_date) {
    const relativeMatch = String(item.estimated_delivery_date).match(
      /^(?:ok\.|około)\s*(\d{1,2})\s*(?:tygodni|tyg\.?)$/i
    );
    if (relativeMatch) {
      estimatedDate = new Date(orderDate);
      estimatedDate.setDate(estimatedDate.getDate() + Number(relativeMatch[1]) * 7);
    }
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let defaultDate = estimatedDate || today;
  if (orderDate && defaultDate < orderDate) defaultDate = orderDate;
  return formatDateInputValue(defaultDate);
};

const ProductionSchucoMonitoring = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'pending';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [duplicateOrderAlerts, setDuplicateOrderAlerts] = useState([]);
  const [complaintCount, setComplaintCount] = useState(0);
  const [markingReceivedKey, setMarkingReceivedKey] = useState(null);
  const [complaintDeliveryDates, setComplaintDeliveryDates] = useState({});

  // Order delivery status lookup
  const [orderIdentifier, setOrderIdentifier] = useState('');
  const [orderStatus, setOrderStatus] = useState(null);
  const [orderStatusError, setOrderStatusError] = useState(null);
  const [orderStatusLoading, setOrderStatusLoading] = useState(false);
  const [orderSuggestions, setOrderSuggestions] = useState([]);
  const [orderSuggestionsLoading, setOrderSuggestionsLoading] = useState(false);
  const [orderSuggestionsOpen, setOrderSuggestionsOpen] = useState(false);
  const [activeOrderSuggestion, setActiveOrderSuggestion] = useState(-1);
  const orderSuggestionFetchIdRef = useRef(0);
  const skipNextOrderSuggestionFetchRef = useRef(false);

  // Monotonic request ids: only the most recently started request may update
  // state, so a slow response for a previous tab cannot overwrite newer data.
  const fetchIdRef = useRef(0);
  const complaintCountFetchIdRef = useRef(0);

  // Item lookup state
  const [lookupItem, setLookupItem] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [sortByLatestChanges, setSortByLatestChanges] = useState(false);

  // Search / filter
  const [searchTerm, setSearchTerm] = useState('');
  // Filtering runs on the debounced value; clearing the box applies immediately.
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const activeSearchTerm = searchTerm.trim() === '' ? '' : debouncedSearchTerm;
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setData([]);
    try {
      let result;
      if (activeTab === 'pending') {
        result = await schucoTrackingService.getPendingItems();
      } else if (activeTab === 'predictions') {
        result = await schucoTrackingService.getPredictions();
      } else if (activeTab === 'complaints') {
        const countFetchId = ++complaintCountFetchIdRef.current;
        result = await schucoTrackingService.getComplaintItems();
        if (countFetchId === complaintCountFetchIdRef.current) {
          setComplaintCount((result || []).length);
        }
      }
      if (fetchId !== fetchIdRef.current) return;
      setData(result || []);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
    // Invalidate the in-flight request on tab change and on unmount.
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  const fetchComplaintCount = useCallback(async () => {
    const countFetchId = ++complaintCountFetchIdRef.current;
    try {
      // Lightweight endpoint: no need to download the whole complaint list.
      const count = await schucoTrackingService.getComplaintCount();
      if (countFetchId !== complaintCountFetchIdRef.current) return;
      setComplaintCount(count || 0);
    } catch (err) {
      if (countFetchId !== complaintCountFetchIdRef.current) return;
      setComplaintCount(0);
    }
  }, []);

  useEffect(() => {
    fetchComplaintCount();
    return () => {
      complaintCountFetchIdRef.current += 1;
    };
  }, [fetchComplaintCount]);

  const fetchDuplicateOrderAlerts = useCallback(async () => {
    try {
      const result = await schucoTrackingService.getPossibleDuplicateOrders();
      setDuplicateOrderAlerts(result || []);
    } catch (err) {
      setDuplicateOrderAlerts([]);
    }
  }, []);

  useEffect(() => {
    fetchDuplicateOrderAlerts();
  }, [fetchDuplicateOrderAlerts]);

  useEffect(() => {
    const query = orderIdentifier.trim();
    const fetchId = ++orderSuggestionFetchIdRef.current;
    if (activeTab !== 'pending') {
      setOrderSuggestions([]);
      setOrderSuggestionsLoading(false);
      setOrderSuggestionsOpen(false);
      setActiveOrderSuggestion(-1);
      return undefined;
    }
    if (skipNextOrderSuggestionFetchRef.current) {
      skipNextOrderSuggestionFetchRef.current = false;
      return undefined;
    }
    if (query.length < 2) {
      setOrderSuggestions([]);
      setOrderSuggestionsLoading(false);
      setOrderSuggestionsOpen(false);
      setActiveOrderSuggestion(-1);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      setOrderSuggestionsLoading(true);
      try {
        const result = await schucoTrackingService.getOrderIdentifierSuggestions(query);
        if (fetchId !== orderSuggestionFetchIdRef.current) return;
        setOrderSuggestions(result || []);
        setOrderSuggestionsOpen((result || []).length > 0);
        setActiveOrderSuggestion(-1);
      } catch (err) {
        if (fetchId !== orderSuggestionFetchIdRef.current) return;
        setOrderSuggestions([]);
        setOrderSuggestionsOpen(false);
      } finally {
        if (fetchId === orderSuggestionFetchIdRef.current) {
          setOrderSuggestionsLoading(false);
        }
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [activeTab, orderIdentifier]);

  // Fetch last sync info on mount
  useEffect(() => {
    const fetchLastSync = async () => {
      try {
        const result = await schucoTrackingService.getLastSync();
        setLastSync(result);
      } catch (err) {
        // Silently fail - no last sync yet
        console.log('No last sync info available');
      }
    };
    fetchLastSync();
  }, []);

  // Reset lookup when tab changes
  useEffect(() => {
    setLookupResult(null);
    setLookupError(null);
    setLookupItem('');
    setSortField(null);
    setSortDir('asc');
    setSortByLatestChanges(false);
    setSearchTerm('');
    setExportMenuOpen(false);
    setMarkingReceivedKey(null);
    setComplaintDeliveryDates({});
  }, [activeTab]);

  // Close export menu when clicking outside
  useEffect(() => {
    if (!exportMenuOpen) return;
    
    const handleClickOutside = (event) => {
      if (!event.target.closest('.om-export-wrapper')) {
        setExportMenuOpen(false);
      }
    };
    
    // Delay attachment to prevent interfering with the opening click
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [exportMenuOpen]);

  const handleItemLookup = async (e) => {
    e.preventDefault();
    if (!lookupItem.trim()) return;
    setLookupError(null);
    setLookupResult(null);
    try {
      const result = await schucoTrackingService.getItemPrediction(lookupItem.trim());
      setLookupResult(result);
    } catch (err) {
      setLookupError(err.response?.data?.detail || 'Item not found');
    }
  };

  const handleOrderStatusLookup = async (e) => {
    e.preventDefault();
    const identifier = orderIdentifier.trim();
    if (!identifier) return;

    setOrderStatusLoading(true);
    setOrderStatus(null);
    setOrderStatusError(null);
    try {
      const result = await schucoTrackingService.getOrderDeliveryStatus(identifier);
      setOrderStatus(result);
    } catch (err) {
      setOrderStatusError(
        err.response?.data?.detail || 'Nie udało się sprawdzić zamówienia Schuco'
      );
    } finally {
      setOrderStatusLoading(false);
    }
  };

  const selectOrderSuggestion = (suggestion) => {
    skipNextOrderSuggestionFetchRef.current = true;
    setOrderIdentifier(suggestion.value);
    setOrderSuggestions([]);
    setOrderSuggestionsOpen(false);
    setActiveOrderSuggestion(-1);
    setOrderStatus(null);
    setOrderStatusError(null);
  };

  const handleOrderIdentifierKeyDown = (event) => {
    if (!orderSuggestionsOpen || orderSuggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveOrderSuggestion(current =>
        current < orderSuggestions.length - 1 ? current + 1 : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveOrderSuggestion(current =>
        current > 0 ? current - 1 : orderSuggestions.length - 1
      );
    } else if (event.key === 'Escape') {
      setOrderSuggestionsOpen(false);
      setActiveOrderSuggestion(-1);
    } else if (event.key === 'Enter' && activeOrderSuggestion >= 0) {
      event.preventDefault();
      selectOrderSuggestion(orderSuggestions[activeOrderSuggestion]);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    setError(null);
    setExportMenuOpen(false);
    try {
      if (activeTab === 'pending') {
        await schucoTrackingService.exportPendingItems(format);
      } else if (activeTab === 'predictions') {
        await schucoTrackingService.exportPredictions(format);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleComplaintReceived = async (item, rowKey) => {
    const itemKey = `${item.order_number}:${item.position}:${item.item_number}`;
    const deliveryDate = (
      complaintDeliveryDates[rowKey]
      || getDefaultComplaintDeliveryDate(item)
    );
    const minimumDate = formatDateInputValue(parsePLDate(item.order_date));

    if (!deliveryDate || (minimumDate && deliveryDate < minimumDate)) {
      setError('Data dostawy nie może być wcześniejsza niż data zamówienia');
      return;
    }
    const confirmed = window.confirm(
      `Czy oznaczyć materiał ${item.item_number} z zamówienia ${item.order_number} jako dostarczony dnia ${deliveryDate}?`
    );
    if (!confirmed) return;

    setMarkingReceivedKey(itemKey);
    setError(null);
    try {
      await schucoTrackingService.markComplaintItemReceived({
        ...item,
        delivery_date: deliveryDate,
      });
      setData(current => current.filter(candidate => (
        `${candidate.order_number}:${candidate.position}:${candidate.item_number}` !== itemKey
      )));
      await fetchComplaintCount();
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się oznaczyć materiału jako dostarczony');
    } finally {
      setMarkingReceivedKey(null);
    }
  };

  // ─── Sorting ───
  const handleSort = (field) => {
    setSortByLatestChanges(false);
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSortByLatestChanges = () => {
    setSortByLatestChanges(prev => !prev);
    if (!sortByLatestChanges) {
      setSortField(null);
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  // ─── Filtering + Sorting pipeline ───
  const processedData = useMemo(() => {
    let items = [...data];

    // filter
    if (activeSearchTerm.trim()) {
      const term = activeSearchTerm.toLowerCase();
      items = items.filter(item =>
        Object.values(item).some(v =>
          String(v).toLowerCase().includes(term)
        )
      );
    }

    // Sort by the newest delivery change: most recently changed items first.
    if (sortByLatestChanges) {
      items.sort((a, b) => {
        const getLatestChange = (item) => {
          const explicitDate = parsePLDate(item.last_change_date);
          if (explicitDate) return explicitDate;

          const historyDates = (item.delivery_date_history || [])
            .map(h => parsePLDate(h.changed_on))
            .filter(Boolean);
          return historyDates.length > 0
            ? new Date(Math.max(...historyDates.map(date => date.getTime())))
            : null;
        };

        const aLatest = getLatestChange(a);
        const bLatest = getLatestChange(b);
        const aHasChanges = Boolean(aLatest);
        const bHasChanges = Boolean(bLatest);
        
        // Items with changes come first
        if (aHasChanges && !bHasChanges) return -1;
        if (!aHasChanges && bHasChanges) return 1;
        if (!aHasChanges && !bHasChanges) return 0;
        
        return bLatest.getTime() - aLatest.getTime();
      });
    } else if (sortField) {
      // Regular field sort
      items.sort((a, b) => {
        let va = a[sortField];
        let vb = b[sortField];

        if (!va && !vb) return 0;
        if (!va) return 1;
        if (!vb) return -1;

        const da = parsePLDate(va);
        const db = parsePLDate(vb);
        if (da && db) {
          return sortDir === 'asc' ? da - db : db - da;
        }

        if (!isNaN(va) && !isNaN(vb)) {
          return sortDir === 'asc' ? va - vb : vb - va;
        }

        va = String(va ?? '').toLowerCase();
        vb = String(vb ?? '').toLowerCase();

        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return items;
  }, [data, activeSearchTerm, sortByLatestChanges, sortField, sortDir]);

  const pagination = usePagination(processedData, {
    storageKey: 'production-schuco-monitoring-page-size',
  });


  // ─── Helper to render delivery date with original ───
  const renderDeliveryDate = (item) => {
    const current = item.estimated_delivery_date;
    const original = item.original_delivery_date;
    
    // No dates at all
    if (!current && !original) return '—';
    
    // Only current date, or dates are the same
    if (!original || current === original) {
      return current || '—';
    }
    
    // Both dates exist and are different - show new date with original below
    return (
      <div style={{ lineHeight: '1.3' }}>
        <div><strong>{current}</strong></div>
        <div style={{ 
          fontSize: '0.85em', 
          color: '#888', 
          textDecoration: 'line-through' 
        }}>
          {original}
        </div>
      </div>
    );
  };

  // ─── Table renderers ───
  const renderPendingTable = () => (
    <table className="om-table">
      <thead>
        <tr>
          <th onClick={() => handleSort('order_number')} style={{ cursor: 'pointer' }} title="Numer zamówienia">Nr zam.{sortIndicator('order_number')}</th>
          <th onClick={() => handleSort('item_number')} style={{ cursor: 'pointer' }} title="Numer materiału">Nr mat.{sortIndicator('item_number')}</th>
          <th onClick={() => handleSort('item_name')} style={{ cursor: 'pointer' }} title="Nazwa materiału">Nazwa{sortIndicator('item_name')}</th>
          <th onClick={() => handleSort('customer_order_designation')} style={{ cursor: 'pointer' }} title="Zamówienie klienta">Zam. klienta{sortIndicator('customer_order_designation')}</th>
          <th onClick={() => handleSort('order_date')} style={{ cursor: 'pointer' }} title="Data zamówienia">Data zam.{sortIndicator('order_date')}</th>
          <th onClick={() => handleSort('estimated_delivery_date')} style={{ cursor: 'pointer' }} title="Szacowana dostawa">Szac. dost.{sortIndicator('estimated_delivery_date')}</th>
          <th onClick={() => handleSort('ordered')} style={{ cursor: 'pointer' }} title="Zamówiono">Zam.{sortIndicator('ordered')}</th>
          <th onClick={() => handleSort('delivered')} style={{ cursor: 'pointer' }} title="Dostarczono">Dost.{sortIndicator('delivered')}</th>
          <th onClick={() => handleSort('remaining')} style={{ cursor: 'pointer' }} title="Pozostało">Pozost.{sortIndicator('remaining')}</th>
        </tr>
      </thead>
      <tbody>
        {pagination.pageItems.map((item, i) => (
          <tr key={i} className={isDelayed(item) ? 'om-row-delayed' : ''}>
            <td>{item.order_number}</td>
            <td><strong>{item.item_number}</strong></td>
            <td>{item.item_name}</td>
            <td>{item.customer_order_designation || '—'}</td>
            <td>{item.order_date}</td>
            <td 
              className={getDeliveryStatusClass(item)}
              title={getDeliveryHistoryTooltip(item)}
              style={{ cursor: item.delivery_date_history?.length > 0 || item.original_delivery_date ? 'help' : 'default' }}
            >
              {renderDeliveryDate(item)}
            </td>
            <td>{item.ordered}</td>
            <td>{item.delivered}</td>
            <td className="om-remaining">{item.remaining}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const updatePredictionCorrection = (correction) => {
    setData(items => items.map(item => (
      item.item_number === correction.item_number ? { ...item, ...correction } : item
    )));
    setLookupResult(item => (
      item?.item_number === correction.item_number ? { ...item, ...correction } : item
    ));
  };

  const renderPredictionsTable = () => (
    <table className="om-table">
      <thead>
        <tr>
          <th onClick={() => handleSort('item_number')} style={{ cursor: 'pointer' }} title="Numer materiału">Nr mat.{sortIndicator('item_number')}</th>
          <th onClick={() => handleSort('item_name')} style={{ cursor: 'pointer' }} title="Nazwa materiału">Nazwa{sortIndicator('item_name')}</th>
          <th onClick={() => handleSort('average_days')} style={{ cursor: 'pointer' }} title="Średnia (dni robocze)">Śred. (dni){sortIndicator('average_days')}</th>
          <th title="Ręczna wartość w dniach z okresem ważności">Korekta</th>
          <th onClick={() => handleSort('median_days')} style={{ cursor: 'pointer' }} title="Mediana (dni robocze)">Med. (dni){sortIndicator('median_days')}</th>
          <th onClick={() => handleSort('samples')} style={{ cursor: 'pointer' }} title="Liczba próbek">Próbki{sortIndicator('samples')}</th>
        </tr>
      </thead>
      <tbody>
        {pagination.pageItems.map(item => (
          <tr key={item.item_number}>
            <td><strong>{item.item_number}</strong></td>
            <td>{item.item_name}</td>
            <td>{item.average_days}</td>
            <td><SchucoPredictionCorrection item={item} onChange={updatePredictionCorrection} /></td>
            <td>{item.median_days}</td>
            <td>{item.samples}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderComplaintsTable = () => (
    <table className="om-table">
      <thead>
        <tr>
          <th onClick={() => handleSort('order_number')} style={{ cursor: 'pointer' }}>Nr zam.{sortIndicator('order_number')}</th>
          <th onClick={() => handleSort('position')} style={{ cursor: 'pointer' }}>Poz.{sortIndicator('position')}</th>
          <th onClick={() => handleSort('item_number')} style={{ cursor: 'pointer' }}>Nr mat.{sortIndicator('item_number')}</th>
          <th onClick={() => handleSort('item_name')} style={{ cursor: 'pointer' }}>Nazwa{sortIndicator('item_name')}</th>
          <th onClick={() => handleSort('customer_order_designation')} style={{ cursor: 'pointer' }}>Zam. klienta{sortIndicator('customer_order_designation')}</th>
          <th onClick={() => handleSort('order_date')} style={{ cursor: 'pointer' }}>Data zam.{sortIndicator('order_date')}</th>
          <th onClick={() => handleSort('estimated_delivery_date')} style={{ cursor: 'pointer' }}>Szac. dost.{sortIndicator('estimated_delivery_date')}</th>
          <th onClick={() => handleSort('remaining')} style={{ cursor: 'pointer' }}>Pozostało{sortIndicator('remaining')}</th>
          <th>Termin dostawy</th>
          <th>Akcja</th>
        </tr>
      </thead>
      <tbody>
        {pagination.pageItems.map((item, i) => {
          const itemKey = `${item.order_number}:${item.position}:${item.item_number}`;
          const rowKey = getComplaintRowKey(item);
          const isMarking = markingReceivedKey === itemKey;
          const minimumDate = formatDateInputValue(parsePLDate(item.order_date));
          const selectedDeliveryDate = (
            complaintDeliveryDates[rowKey]
            || getDefaultComplaintDeliveryDate(item)
          );
          return (
            <tr key={`${itemKey}:${item.estimated_delivery_date || i}`} className={isDelayed(item) ? 'om-row-delayed' : ''}>
              <td>{item.order_number}</td>
              <td>{item.position || '—'}</td>
              <td><strong>{item.item_number}</strong></td>
              <td>{item.item_name}</td>
              <td>{item.customer_order_designation || '—'}</td>
              <td>{item.order_date}</td>
              <td
                className={getDeliveryStatusClass(item)}
                title={getDeliveryHistoryTooltip(item)}
              >
                {renderDeliveryDate(item)}
              </td>
              <td className="om-remaining">{item.remaining}</td>
              <td>
                <input
                  type="date"
                  className="om-delivery-date-input"
                  min={minimumDate || undefined}
                  value={selectedDeliveryDate}
                  onChange={event => setComplaintDeliveryDates(current => ({
                    ...current,
                    [rowKey]: event.target.value,
                  }))}
                  aria-label={`Termin dostawy materiału ${item.item_number}`}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="om-received-btn"
                  disabled={Boolean(markingReceivedKey)}
                  onClick={() => handleComplaintReceived(item, rowKey)}
                >
                  <CheckCircle2 size={16} aria-hidden="true" />
                  {isMarking ? 'Zapisywanie…' : 'Dojechał'}
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderTable = () => {
    if (activeTab === 'pending') return renderPendingTable();
    if (activeTab === 'predictions') return renderPredictionsTable();
    if (activeTab === 'complaints') return renderComplaintsTable();
    return null;
  };

  return (
  <div className="page-container order-monitoring">
    <div className="page-header">
      <h2 className="module-page-title"><Boxes aria-hidden="true" /> Monitorowanie zamówień Schüco</h2>
      <p className="header-description">
        Śledzenie zamówień Schüco, pozycji reklamacyjnych i przewidywanych terminów dostaw.
      </p>
    </div>
      <div className="page-content">
        {/* Toolbar */}
        <div className="om-toolbar" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '1rem', minWidth: 0 }}>
          <div className="om-tabs" style={{ display: 'flex', flexWrap: 'nowrap', flexShrink: 0, minWidth: 0 }}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`om-tab ${activeTab === tab.key ? 'om-tab--active' : ''}`}
                onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', tab.key); return next; })}
                style={{ whiteSpace: 'nowrap' }}
              >
                <span>{tab.label}</span>
                {tab.key === 'complaints' && complaintCount > 0 && (
                  <span
                    className="om-tab-count"
                    aria-label={`${complaintCount} oczekujących reklamacji`}
                  >
                    {complaintCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="om-controls" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            {activeTab === 'pending' && (
              <button
                className={`om-sync-btn ${sortByLatestChanges ? 'om-btn-active' : ''}`}
                onClick={handleSortByLatestChanges}
                title="Sortowanie według ostatnich zmian terminów dostaw."
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {sortByLatestChanges ? '✓ ' : ''}Ostatnie zmiany
              </button>
            )}

            {activeTab !== 'complaints' && <div
              className="om-export-wrapper"
              style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}
            >
              <button
                className="om-sync-btn"
                disabled={exporting || loading || processedData.length === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setExportMenuOpen(!exportMenuOpen);
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                <Download size={16} aria-hidden="true" />
                Export
                {exportMenuOpen
                  ? <ChevronUp size={15} aria-hidden="true" />
                  : <ChevronDown size={15} aria-hidden="true" />}
              </button>
              {exportMenuOpen && (
                <div
                  className="om-export-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 9999,
                    background: '#fff',
                    border: '1px solid #ccc',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '160px',
                    width: '160px',
                    padding: '8px 0',
                    display: 'block',
                  }}
                >
                  <button style={{ width: '100%' }} onClick={() => handleExport('csv')}>CSV</button>
                  <button style={{ width: '100%' }} onClick={() => handleExport('xlsx')}>Excel (.xlsx)</button>
                </div>
              )}
            </div>}
          </div>
        </div>

        {/* Last sync info */}
        {lastSync && (
          <div className="om-banner om-banner--info" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}>
            Ostatnia synchronizacja: {new Date(lastSync.synced_at).toLocaleString()}
            {' '}({lastSync.new_emails_processed} email(s), {lastSync.items_saved} item(s))
          </div>
        )}

        {activeTab === 'pending' && (
          <>
            <form className="om-order-lookup" onSubmit={handleOrderStatusLookup}>
              <div className="om-order-autocomplete">
                <input
                  type="text"
                  role="combobox"
                  className="om-search"
                  placeholder="Wpisz Nr zam. lub Zam. klienta"
                  aria-label="Numer zamówienia Schuco lub zamówienie klienta"
                  aria-autocomplete="list"
                  aria-controls="schuco-order-identifier-suggestions"
                  aria-expanded={orderSuggestionsOpen}
                  aria-activedescendant={
                    activeOrderSuggestion >= 0
                      ? `schuco-order-identifier-suggestion-${activeOrderSuggestion}`
                      : undefined
                  }
                  autoComplete="off"
                  value={orderIdentifier}
                  onChange={e => {
                    setOrderIdentifier(e.target.value);
                    setOrderStatus(null);
                    setOrderStatusError(null);
                  }}
                  onFocus={() => {
                    if (orderSuggestions.length > 0) setOrderSuggestionsOpen(true);
                  }}
                  onBlur={() => setOrderSuggestionsOpen(false)}
                  onKeyDown={handleOrderIdentifierKeyDown}
                />
                {orderSuggestionsOpen && (
                  <ul
                    id="schuco-order-identifier-suggestions"
                    className="om-order-suggestions"
                    role="listbox"
                  >
                    {orderSuggestions.map((suggestion, index) => (
                      <li
                        id={`schuco-order-identifier-suggestion-${index}`}
                        key={`${suggestion.kind}:${suggestion.value}`}
                        role="option"
                        aria-selected={index === activeOrderSuggestion}
                        className={index === activeOrderSuggestion ? 'is-active' : ''}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => selectOrderSuggestion(suggestion)}
                      >
                        <strong>{suggestion.value}</strong>
                        <span>
                          {suggestion.kind === 'order_number'
                            ? 'Numer zamówienia'
                            : `Zamówienie klienta · Nr zam. ${suggestion.order_number}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {orderSuggestionsLoading && (
                  <span className="om-order-suggestions-loading" aria-live="polite">
                    Szukanie…
                  </span>
                )}
              </div>
              <button
                type="submit"
                className="om-order-lookup-btn"
                disabled={orderStatusLoading || !orderIdentifier.trim()}
                title="Sprawdź status dostawy zamówienia Schuco"
              >
                <Search size={18} aria-hidden="true" />
                {orderStatusLoading ? 'Sprawdzanie...' : 'Sprawdź zamówienie'}
              </button>
            </form>

            <div aria-live="polite">
              {orderStatus?.map(item => (
                <div
                  key={item.order_number}
                  className={`om-banner ${item.all_delivered ? 'om-banner--success' : 'om-banner--info'}`}
                >
                  <strong>Nr zam. {item.order_number}</strong>
                  {item.customer_order_designation && ` (Zam. klienta: ${item.customer_order_designation})`}
                  {' — '}
                  {item.all_delivered
                    ? 'całe zamówienie zostało dostarczone.'
                    : item.last_item_date
                      ? `ostatni materiał jest planowany na ${item.last_item_date}.`
                      : 'zamówienie nie zostało jeszcze dostarczone w całości; brak potwierdzonego terminu ostatniego materiału.'}
                </div>
              ))}
              {orderStatusError && (
                <div className="om-banner om-banner--error">{orderStatusError}</div>
              )}
            </div>
          </>
        )}

        {/* Error banner */}
        {error && (
          <div className="om-banner om-banner--error">⚠️ {error}</div>
        )}

        {duplicateOrderAlerts.map(alert => (
          <div
            key={`${alert.order_number}:${alert.similar_order_number}`}
            className="om-banner om-banner--warning"
            role="alert"
          >
            <strong>⚠️ Możliwe podwójne zamówienie Schuco:</strong>{' '}
            zamówienie <strong>{alert.order_number}</strong> z {alert.order_date} jest w{' '}
            <strong>{alert.similarity_percent}%</strong> podobne do zamówienia{' '}
            <strong>{alert.similar_order_number}</strong> z {alert.similar_order_date}
            {' '}({alert.shared_materials} z {alert.compared_materials} wspólnych materiałów).
            {' '}Zweryfikuj zamówienia przed dalszą realizacją.
          </div>
        ))}

        {/* Search bar */}
        <div className="om-search-row">
          <input
            type="text"
            className="om-search"
            placeholder="Wyszukaj po wszystkich kolumnach"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          {/* Item lookup — only on predictions tab */}
          {activeTab === 'predictions' && (
            <form className="om-lookup-form" onSubmit={handleItemLookup}>
              <input
                type="text"
                className="om-lookup-input"
                placeholder="Numer materiału"
                value={lookupItem}
                onChange={e => setLookupItem(e.target.value)}
              />
              <button type="submit" className="om-lookup-btn">🔍</button>
            </form>
          )}
        </div>

        {/* Lookup result */}
        {lookupResult && (
          <div className="om-banner om-banner--info">
            <strong>{lookupResult.item_number}</strong> — Avg: {lookupResult.average_days} days, Median: {lookupResult.median_days} days ({lookupResult.samples} samples)
            <div>
              Korekta:
              <SchucoPredictionCorrection key={lookupResult.item_number} item={lookupResult} onChange={updatePredictionCorrection} />
            </div>
          </div>
        )}
        {lookupError && (
          <div className="om-banner om-banner--error">{lookupError}</div>
        )}

        {/* Data table */}
        <div className="om-table-wrap">
          {loading ? (
            <div className="om-loading">Loading data…</div>
          ) : processedData.length === 0 ? (
            <div className="om-empty">
              {activeTab === 'complaints'
                ? 'Brak oczekujących materiałów z reklamacji.'
                : 'Brak danych.'}
            </div>
          ) : (
            <>
              {renderTable()}
              <TablePagination pagination={pagination} unfilteredTotal={data.length} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductionSchucoMonitoring;
